/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : RolesAndAccess.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
/**
 * RolesAndAccess.jsx
 *
 * This component displays the Role Management page.
 * It uses server-side pagination, sorting, and filtering.
 * Redux is the single source of truth for data.
 */

import React, { useEffect, useMemo, useState } from "react";

/* ===========================
   MUI imports (UI only)
   =========================== */
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  Chip,
} from "@mui/material";
import CustomFilterTemplate from "../../components/customComponentsV2/common/filter/CustomFilterTemplate";
import SortTemplate from "../../components/customComponentsV2/common/Templates/SortTemplate";
/* ===========================
   Icons
   =========================== */
import BorderColorIcon from "@mui/icons-material/BorderColor";
import AttachmentIcon from "@mui/icons-material/Attachment";
import CloseIcon from "@mui/icons-material/Close";

/* ===========================
   Redux
   =========================== */
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import { HandleApiActions, userManagementApis } from "../../redux/actions";
import policiesActions from "../../redux/actions/policiesActions";

/* ===========================
   Custom Components
   =========================== */
import CommonBreadcrumbs from "../../components/customComponentsV2/common/Breadcrumbs/CommonBreadcrumbs";
import CustomSearchbarWithApiV2 from "../../components/customComponentsV2/common/searchBar/CustomSearchbarWithApiV2";
import AddButtonV2 from "../../components/customComponentsV2/common/button/AddButtonV2";
import CustomDataGridV2 from "../../components/customComponentsV2/common/datagrid/CustomDataGridV2";
import CustomDialogBoxV2 from "../../components/customComponentsV2/common/customDialogBox/CustomDialogBoxV2";
import CustomTabsV2 from "../../components/customComponentsV2/common/tabs/CustomTabsV2";

/* ===========================
   Feature Components
   =========================== */
import AddRole from "./role/AddRole";
import LegalStandardChapter from "./role/LegalStandardChapter";

/* ===========================
   Utils
   =========================== */
import { convertUtcToTimeZone } from "../../utils/convertUtcToTimeZone";

/* ===========================
   Assets
   =========================== */
import deleteIcon from "../../assets/delete_icon.png";
import CustomButtonV2 from "../../components/customComponentsV2/common/button/CustomButtonV2";
import PoliciesTab from "./role/PoliciesTab";
import dayjs from "dayjs";
import roletypeActions from "../../redux/actions/roletypeActions";

/* ======================================================================
   MAIN COMPONENT
   ====================================================================== */

function RolesAndAccess({ setIsAddRole, isAddRole }) {
  /* --------------------------------------------------
     Redux hooks
     -------------------------------------------------- */
  const dispatch = useDispatch();

  /* --------------------------------------------------
     Pull role list + meta from Redux
     Redux is the ONLY source of truth
     -------------------------------------------------- */
  const { rolesListMain, rolePolicy } = useSelector(
    (state) => state.userManagementReducer,
    shallowEqual,
  );

  /* --------------------------------------------------
     Pull policies list (used in attach/detach dialog)
     -------------------------------------------------- */
  const policies = useSelector((state) => state.policyReducer.policies);

  /* --------------------------------------------------
     Pull profile details (timezone conversion)
     -------------------------------------------------- */
  const profileDetails = useSelector((state) => state.profileReducer);

  /* ==================================================
     PAGINATION STATE (server-side)
     ================================================== */

  // Current page (1-based for UI)
  const [page, setPage] = useState(1);

  // Number of rows per page
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Total roles count (used by custom table & pagination)
  const [rolesCount, setRolesCount] = useState(0);
  const [triggerRoles, setTriggerRoles] = useState(false);

  /* ==================================================
     SORTING STATE (server-side)
     ================================================== */

  const [selectedSortColumn, setSelectedSortColumn] = useState("created_at");
  const [selectedSortOrder, setSelectedSortOrder] = useState("desc");

  /* ==================================================
     SEARCH STATE (server-side)
     ================================================== */

  const [selectedSearchField, setSelectedSearchField] = useState("name");

  const [searchFieldValue, setSearchFieldValue] = useState("");

  /* ==================================================
     UI STATE
     ================================================== */

  // Drawer for edit role
  const [openDrawer, setOpenDrawer] = useState(false);

  // Role selected for edit / policy attach
  const [selectedRole, setSelectedRole] = useState(null);

  // Delete confirmation dialog
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);

  // Attach/detach dialog
  const [openAttachDetachDialog, setOpenAttachDetachDialog] = useState(false);

  /* ==================================================
     POLICY ATTACH STATE
     ================================================== */

  // Selected role policies
  const [selectedPolicies, setSelectedPolicies] = useState([]);

  // Selected policy ids for chip selector
  const [policyDefIds, setPolicyDefIds] = useState("");
  const [onlyTAReviewer, setOnlyTAReviewer] = useState(null);

  /* ==================================================
     DERIVED DATA FROM REDUX
     ================================================== */

  // Rows returned by API (already paginated)
  const roles = rolesListMain?.roles || [];

  // Total rows count from API meta
  const totalCount = rolesListMain?.meta?.total_count || 0;

  /* ==================================================
     MAP API ROLES → TABLE ROWS
     NO slicing, NO sorting here
     ================================================== */

  const tableRows = useMemo(() => {
    return roles.map((role, index) => ({
      ...role,

      // Serial number across pages
      id: (page - 1) * rowsPerPage + index + 1,

      // Convert UTC timestamps to user timezone
      // created_at: convertUtcToTimeZone(
      //   role.created_at,
      //   profileDetails?.profile?.profile?.region,
      // ),
      created_at: dayjs(role.created_at)?.format("DD MMMM YYYY, hh:mm A") || "",

      updated_at: convertUtcToTimeZone(
        role.updated_at,
        profileDetails?.profile?.profile?.region,
      ),
    }));
  }, [roles, page, rowsPerPage, profileDetails]);

  const listOfPolicies = useMemo(() => {
    return policies?.data?.policies;
  }, [policies?.data]);
  const [selectedSortObjBeforeClick, setSelectedSortObjBeforeClick] = useState({
    sortBy: "created_at",
    sortOrder: "DESC",
  });
  const [selectedSortObjAfterClick, setSelectedSortObjAfterClick] = useState({
    sortBy: "created_at",
    sortOrder: "DESC",
  });


  /* ==================================================
     FETCH ROLES FROM API (SERVER-SIDE)
     ================================================== */

  const fetchRoles = () => {
    setTriggerRoles((prev) => !prev);
    // dispatch(
    //   HandleApiActions({
    //     ...userManagementApis.list_role_ddh_ucm,

    //     // API parameters
    //     params: {
    //       limit: rowsPerPage,
    //       isActive: true,

    //       // Convert page → offset
    //       offset: page,

    //       sortBy: selectedSortColumn,
    //       sortOrder: selectedSortOrder,

    //       ...(searchFieldValue && {
    //         [selectedSearchField]: searchFieldValue,
    //       }),
    //     },

    //     show_toast: false,
    //   }),
    // );
  };

  useEffect(() => {
    if (!selectedRole?.role_id) return;

    const fetchPolicies = async () => {
      try {
        const resp = await dispatch(
          HandleApiActions({
            ...userManagementApis.get_roleRP_ddh_ucm,
            api_name: `/UCM/role-policy/${Number.parseInt(
              selectedRole.role_id,
            )}/policies`,
          }),
        );

        if (!resp?.error) {
          console.log("Policies:", resp?.data?.rolePolicies);

          setSelectedPolicies(
            resp?.data?.rolePolicies?.map((data) => ({
              label: data?.policy_key,
              value: data?.policy_def_id,
              actions: data?.actions,
            })) || [],
          );
        }
      } catch (error) {
        console.error("Error fetching policies:", error);
      }
    };

    fetchPolicies();
  }, [dispatch, selectedRole]);


  const handleAttach = (data) => {
    console.log("Handle Attach", data);
    console.log("selectedRoleIdselectedRoleId", selectedRole);

    let reqObj = {
      policyIds: [data],
      reason: "Attaching Policy",
    };

    dispatch(
      HandleApiActions({
        ...userManagementApis.add_roleRP_ddh_ucm,
        api_name: `/UCM/role-policy/${Number.parseInt(selectedRole?.role_id)}/policies`,
        params: reqObj,
        show_toast: true,
      }),
    ).then(async () => {
      const resp = await dispatch(
        HandleApiActions({
          ...userManagementApis.get_roleRP_ddh_ucm,
          api_name: `/UCM/role-policy/${Number.parseInt(selectedRole?.role_id)}/policies`,
        }),
      );
      if (!resp?.error) {
        console.log(
          "respdbsicgshsdkovbdsjvdshvldssdv",
          resp?.data?.rolePolicies,
        );
        setSelectedPolicies(
          resp?.data?.rolePolicies?.map((data) => {
            return {
              label: data?.policy_key,
              value: data?.policy_def_id,
              actions: data?.actions,
            };
          }),
        );
      }
    });
  };
  console.log("rolePolicyrolePolicyrolePolicyrolePolicy", rolePolicy);
  console.log(
    "policyDefIdspolicyDefIdspolicyDefIdspolicyDefIdspolicyDefIdspolicyDefIds",
    policyDefIds,
  );
  const handleDetach = (data) => {
    console.log("Handle Detach", data);
    let reqObj = {
      policyIds: [data],
      reason: "Attaching Policy",
    };

    dispatch(
      HandleApiActions({
        ...userManagementApis.delete_roleRP_ddh_ucm,
        api_name: `/UCM/role-policy/${Number.parseInt(selectedRole?.role_id)}/policies`,
        params: reqObj,
        show_toast: true,
      }),
    ).then(async () => {
      const resp = await dispatch(
        HandleApiActions({
          ...userManagementApis.get_roleRP_ddh_ucm,
          api_name: `/UCM/role-policy/${Number.parseInt(selectedRole?.role_id)}/policies`,
        }),
      );
      if (!resp?.error) {
        console.log(
          "respdbsicgshsdkovbdsjvdshvldssdv",
          resp?.data?.rolePolicies,
        );
        setSelectedPolicies(
          resp?.data?.rolePolicies?.map((data) => {
            return {
              label: data?.policy_key,
              value: data?.policy_def_id,
              actions: data?.actions,
            };
          }),
        );
      }
    });
  };

  useEffect(() => {
    // Ensure we only update when API actually returned meta
    if (rolesListMain?.meta?.total_count !== undefined) {
      setRolesCount(rolesListMain.meta.total_count);
    }
  }, [rolesListMain?.meta?.total_count]);

  /* --------------------------------------------------
     Trigger API call whenever pagination/sort/search changes
     -------------------------------------------------- */
  useEffect(() => {
    fetchRoles();
  }, [
    page,
    rowsPerPage,
    selectedSortColumn,
    selectedSortOrder,
    searchFieldValue,
  ]);

  /* ==================================================
     FETCH POLICIES ONCE
     ================================================== */

  useEffect(() => {
    dispatch(
      HandleApiActions({
        ...policiesActions.get_policies,
      }),
    );
  }, []);

  const fetchRoleTypes = async () => {
    return await dispatch(
      HandleApiActions({
        ...roletypeActions.get_all_roletype,
        params: { page_size: 100, page: 1 },
      }),
    );
  };
  const handleTAReviewerDetails = async (data) => {
    try {
      console.log("datatshdhadas", data);

      const roleTypeDetails = await fetchRoleTypes();
      const filteredRoleType = roleTypeDetails?.data?.roleTypes?.find(
        (query) =>
          query?.display_name?.toUpperCase() ===
          data?.role_type_name?.toUpperCase(),
      );
      console.log(
        "filteredRoleTypefilteredRoleTypefilteredRoleType",
        filteredRoleType,
      );
      setOnlyTAReviewer(filteredRoleType);
      console.log("fetchRoleTypesfetchRoleTypes", roleTypeDetails);
    } catch (error) {
      console.error("Error in handleTAReviewerDetails:", error);
    }
  };
  /* ==================================================
     TABLE COLUMNS (unchanged UI)
     ================================================== */

  const COLUMNS = [
    {
      field: "id",
      headerName: "S.No",
      width: 70,
    },
    {
      field: "name",
      headerName: "Role",
      flex: 1,
    },
    {
      field: "description",
      headerName: "Organization",
      flex: 1,
    },
    {
      field: "role_type_name",
      headerName: "Role Type",
      flex: 1,
    },
    {
      field: "created_at",
      headerName: "Created At",
      flex: 1,
    },
    {
      field: "is_system",
      headerName: "Type",
      width: 120,
      renderCell: ({ value }) => (
        <Chip
          size="small"
          label={value ? "System" : "Custom"}
          variant={value ? "outlined" : "filled"}
          color={value ? "default" : "success"}
          sx={{ borderRadius: 0 }}
        />
      ),
    },
    {
      field: "is_active",
      headerName: "Status",

      renderCell: (params) => {
        const value = params.value;

        return (
          <Chip
            size="small"
            label={value ? "Active" : "Inactive"}
            variant={value ? "filled" : "outlined"}
            color={value ? "success" : "default"}
            sx={{ borderRadius: 0 }}
          />
        );
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      sortable: false,
      width: 160,
      renderCell: ({ row }) => {
        const blocked = row.is_system || !row.is_assignable;

        return (
          <Box sx={{ display: "flex", gap: 0.5 }}>
            {/* Edit */}
            <Tooltip title="Edit Role">
              <span>
                <IconButton
                  disabled={blocked}
                  onClick={() => {
                    setSelectedRole(row);
                    setOpenDrawer(true);
                  }}
                >
                  <BorderColorIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>

            {/* Delete */}
            <Tooltip title="Delete Role">
              <span>
                <IconButton
                  disabled={blocked}
                  onClick={() => {
                    setSelectedRole(row);
                    setOpenDeleteDialog(true);
                  }}
                >
                  <img alt="Delete" src={deleteIcon} width={18} />
                </IconButton>
              </span>
            </Tooltip>

            {/* Policies */}
            <Tooltip title="Manage Policies">
              <span>
                <IconButton
                  // disabled={blocked}
                  onClick={() => {
                    setSelectedRole(row);
                    setOpenAttachDetachDialog(true);
                    handleTAReviewerDetails(row);
                  }}
                >
                  <AttachmentIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        );
      },
    },
  ];

  /* ==================================================
     RENDER
     ================================================== */
  console.log("SelectedRoleSelectedRole", selectedRole);
  return (
    <Box
      sx={{
        ...styles.rcMainContainer,
        height: "95%",
        minHeight: 0,
        overflow: "hidden",
        p: 0.5,
      }}
    >
      {/* =======================
        Breadcrumbs (unchanged)
        ======================= */}
      <CommonBreadcrumbs
        items={[
          { label: "Settings" },
          { label: "Accounts & Access" },
          { label: "Role Management" },
        ]}
      />

      {/* =======================
        Top toolbar row
        (THIS CONTROLS TOP SPACING)
        ======================= */}
      <Box sx={styles.tblFun}>
        <Box sx={{ display: "flex" }}>
          {/* Search bar */}
          <CustomFilterTemplate
            handleDiscard={() => {
              setSelectedSortObjBeforeClick({
                sortBy: "created_at",
                sortOrder: "DESC",
              });
            }}
            handleClickApply={() => {
              //  fetchUserDetails(page,rowsPerPage,selectedSortObjBeforeClick?.sortBy,selectedSortObjBeforeClick.sortOrder)
              setSelectedSortObjAfterClick(selectedSortObjBeforeClick);
            }}
            sections={[
              {
                heading: "Sorting",
                content: (
                  <SortTemplate
                    defaultValue={selectedSortObjBeforeClick}
                    handleOnChange={(sortValue) => {
                      setSelectedSortObjBeforeClick(sortValue);
                    }}
                  />
                ),
              },

              //                   {
              //                      heading: "Filter By Active",
              //                     content: (
              //                        <Box
              //                                               sx={{ display: "flex", flexDirection: "column", mt: 2 }}
              //                                             >
              //                                             {[
              //   { value: true, label: "Enable" },
              //   { value: false, label: "Disable" },
              // ].map((item) => (
              //   <CustomCheckBoxV2
              //     key={item.label}
              //     label={item.label}
              //     value={roleStatus === item.value}
              //     onChange={(e) => {
              //       const checked = e.target.checked;

              //       if (checked) {
              //         setRoleStatus(item.value);
              //       } else {
              //         setRoleStatus(null);
              //       }
              //     }}
              //   />
              // ))}
              //                                             </Box>
              //                     )
              //                   }
            ]}
          />
          <Box sx={{ ml: 2, mt: 0.3 }}>
            <CustomSearchbarWithApiV2
              apiAction={{ ...userManagementApis.list_role_ddh_ucm }}
              params={{
                isActive: true,
                offset: page,
                limit: rowsPerPage,
                sortBy: selectedSortObjAfterClick.sortBy,
                sortOrder: selectedSortObjAfterClick.sortOrder,
                ...(searchFieldValue && {
                  [selectedSearchField]: searchFieldValue,
                }),
              }}
              triggerApi={triggerRoles}
              searchKey="name"
              size="small"
              type="text"
              variant="outlined"
              placeholder="Search Roles..."
              valueError={""}
              isMultiline={false}
              minRows={1}
              maxRows={3}
              disabled={false}
              setSearchFieldValue={setSearchFieldValue}
              txtFldStyle={{
                width: "100%",
              }}
              getDataOnSearch={(data, resp) => {
                setRolesCount(data?.meta?.total_count);
              }}
            />
          </Box>
        </Box>

        {/* Add button */}
        <Box sx={{ mt: 1, mb: 1 }}>
          <AddButtonV2 handleOnClick={() => setIsAddRole(true)} />
        </Box>
      </Box>

      {/* =======================
        Content padding wrapper
        (THIS CREATES AIR AROUND TABLE)
        ======================= */}
      <Box sx={{ p: 1 }}>
        <Box>
          {/* =======================
            Table container
            (HEIGHT + BOTTOM SPACING)
            ======================= */}
          <Box
            sx={{
              height: "76vh",
              width: "100%",
              display: "flex",
              flexDirection: "column",
              mt: 1,
            }}
          >
            {/* ⬇KEEP YOUR EXISTING CustomDataGridV2 HERE  */}
            <CustomDataGridV2
              rows={tableRows}
              columns={COLUMNS}
              page={page}
              rowsPerPage={rowsPerPage}
              totalItems={totalCount}
              showPagination={totalCount > 0}
              handlePageChange={setPage}
            />
          </Box>
        </Box>
      </Box>

      {/* =======================
        Dialogs / Drawers
        (unchanged placement)
        ======================= */}

      {/* Add / Edit Role */}
      {(openDrawer || isAddRole) && (
        <AddRole
          open={openDrawer || isAddRole}
          rolesAndAccessData={selectedRole}
          onClose={() => {
            setOpenDrawer(false);
            setIsAddRole(false);
          }}
          title={isAddRole ? "Add Role" : "Update Role"}
          handleGetRolesAndAccessList={fetchRoles}
        />
      )}

      {/* Delete Dialog */}
      <CustomDialogBoxV2
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
        title="Delete Role"
        successButtonName={"Ok"}
        rejectButtonName={"Cancel"}
        onClickReject={() => setOpenDeleteDialog(false)}
        actionButtonStyle={"continuous"}
        onClickSuccess={() => {
          dispatch(
            HandleApiActions({
              ...userManagementApis.delete_role_ddh_ucm,
              api_name: `/UCM/roles/${selectedRole.role_id}`,
              show_toast: true,
            }),
          ).then(fetchRoles);
          setOpenDeleteDialog(false);
        }}
      >
        Are you sure you want to delete this role?
      </CustomDialogBoxV2>

      {/* Attach / Detach Dialog */}
      {openAttachDetachDialog && (
        <Dialog
          open
          onClose={() => setOpenAttachDetachDialog(false)}
          fullWidth
          maxWidth="md"
        >
          <DialogTitle>
            Manage Policies
            <IconButton
              onClick={() => setOpenAttachDetachDialog(false)}
              sx={{ float: "right" }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>

          <DialogContent>
            <CustomTabsV2
              tabs={[
                {
                  name: "Policies",
                  content: (
                    <PoliciesTab
                      selectedRole={selectedRole}
                      selectedPolicies={selectedPolicies}
                      setSelectedPolicies={setSelectedPolicies}
                      policyDefIds={policyDefIds}
                      setPolicyDefIds={setPolicyDefIds}
                      listOfPolicies={listOfPolicies}
                      handleAttach={handleAttach}
                      handleDetach={handleDetach}
                    />
                  ),
                },
                {
                  name: "Legal Standards",
                  content:
                    onlyTAReviewer?.capability_code === "TA_REVIEWER" ? (
                      <LegalStandardChapter selectedRoleId={selectedRole} />
                    ) : (
                      "User is not a reviewer"
                    ),
                },
              ]}
            />
          </DialogContent>

          <DialogActions>
            <CustomButtonV2
              type="cancel"
              btnName="Cancel"
              handleOnClick={() => setOpenAttachDetachDialog(false)}
            />
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
}

// Styling for the RolesAndAccess component
const styles = {
  container: {
    width: "100%",
    overflow: "hidden",
  },
  paginationContainer: {
    display: "flex",
  },
  btnsContainer: {
    display: "flex",
    gap: 1,
  },
  tblFun: {
    display: "flex",
    justifyContent: "space-between",
    ml: 1,
    mr: 1,
    mt: 1,
  },
  customButton: {
    ml: 2,
    // borderRadius: "0px",
  },
  UDRSDContainer: {
    width: "100%",

    overflow: "hidden",
  },
  rcPaper: {
    height: 370,
    width: "100%",
    mb: 14,
    scrollbarColor: "#888 #f1f1f1",
    // maxWidth: { xs: "350px", sm: "400px", md: "100%" },
    // overflow: "auto",
  },
  paginationPaper: {
    display: "flex",
    justifyContent: "flex-end",
    padding: "10px",
  },
  // rcMainContainer: {
  //   marginBottom: "60px",
  // },
};

export default RolesAndAccess;

/* ==================================================
   RECENT EDITS (for reference)
   ================================================== 
     return (
    <Box
      sx={{
        height: "95%",
        px: 2, // horizontal breathing space
        py: 1, // vertical breathing space
      }}
    >
      {/* Breadcrumb 
      <Box sx={{ mb: 1.5 }}>
        <CommonBreadcrumbs
          items={[
            { label: "Settings" },
            { label: "Accounts & Access" },
            { label: "Role Management" },
          ]}
        />
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1.5, //  restores gap before table
        }}
      >
        <Box sx={{ ml: 2, mt: 0.3 }}>
          <CustomSearchbarWithApiV2
            apiAction={{ ...userManagementApis.list_role_ddh_ucm }}
            params={{
              isActive: true,
              offset: page,
              limit: rowsPerPage,
              sortBy: selectedSortColumn,
              sortOrder: selectedSortOrder,
              ...(searchFieldValue && {
                [selectedSearchField]: searchFieldValue,
              }),
            }}
            searchKey="name"
            size="small"
            type="text"
            variant="outlined"
            placeholder="Search Roles..."
            valueError={""}
            isMultiline={false}
            minRows={1}
            maxRows={3}
            disabled={false}
            setSearchFieldValue={setSearchFieldValue}
            txtFldStyle={{
              width: "100%",
            }}
            getDataOnSearch={(data, resp) => {
              setRolesCount(data?.meta?.total_count);
            }}
          />
        </Box>

        <AddButtonV2 handleOnClick={() => setIsAddRole(true)} />
      </Box>

      <CustomDataGridV2
        rows={tableRows}
        columns={COLUMNS}
        page={page}
        rowsPerPage={rowsPerPage}
        totalItems={totalCount}
        showPagination={totalCount > 0}
        handlePageChange={setPage}
      />

      {(openDrawer || isAddRole) && (
        <AddRole
          open={openDrawer || isAddRole}
          rolesAndAccessData={selectedRole}
          onClose={() => {
            setOpenDrawer(false);
            setIsAddRole(false);
          }}
          handleGetRolesAndAccessList={fetchRoles}
        />
      )}

      <CustomDialogBoxV2
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
        title="Delete Role"
        onClickSuccess={() => {
          dispatch(
            HandleApiActions({
              ...userManagementApis.delete_role_ddh_ucm,
              api_name: `/UCM/roles/${selectedRole.role_id}`,
            }),
          ).then(fetchRoles);
          setOpenDeleteDialog(false);
        }}
      >
        Are you sure you want to delete this role?
      </CustomDialogBoxV2>

      {openAttachDetachDialog && (
        <Dialog
          open
          onClose={() => setOpenAttachDetachDialog(false)}
          fullWidth
          maxWidth="md"
        >
          <DialogTitle>
            Manage Policies
            <IconButton
              onClick={() => setOpenAttachDetachDialog(false)}
              sx={{ float: "right" }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>

          <DialogContent>
            <CustomTabsV2
              tabs={[
                {
                  name: "Policies",
                  content: (
                    <ChipsSelectField
                      isAttachDetach
                      listValues={selectedPolicies}
                      setListValues={setSelectedPolicies}
                      selectedValue={policyDefIds}
                      setSelectedValue={setPolicyDefIds}
                    />
                  ),
                },
                {
                  name: "Legal Standards",
                  content: (
                    <LegalStandardChapter selectedRoleId={selectedRole} />
                  ),
                },
              ]}
            />
          </DialogContent>

          <DialogActions>
            <IconButton onClick={() => setOpenAttachDetachDialog(false)}>
              Close
            </IconButton>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
  */
